/**
 * Which tool or model produced what came back from a file.
 *
 * The badge carries the distinction that decides whether the text needs
 * proofreading: a tool copied the characters out of the document and cannot have
 * invented one, a model read a picture and can. Shown wherever an import result is
 * shown, and the same fields go to the audit log — `describeEngines` in
 * worker/ai.js writes that line out of this same list, so what the importer says
 * and what the log records can never drift apart.
 */
import { Badge } from './ui'

const STEP = { text: 'Text', pictures: 'Pictures', cleanup: 'Clean-up' }

/** The per-step particulars, only where the reader reported any. */
const detailsOf = (e) => {
  const out = []
  if (e.detail) out.push(e.detail)
  if (e.pages) out.push(`${e.pages} page${e.pages === 1 ? '' : 's'}`)
  if (e.count) out.push(`${e.count} picture${e.count === 1 ? '' : 's'}`)
  if (e.prompt) out.push(`prompt “${e.prompt}”`)
  return out
}

export function ExtractionEngines({ engines, label = 'What read this file' }) {
  if (!engines?.length) return null
  return (
    <div className="engines">
      <div className="engines-head">{label}</div>
      {engines.map((e, i) => (
        <div className="engine" key={`${e.step}-${i}`}>
          <Badge tone={e.kind === 'model' ? 'warn' : 'ok'}>
            {e.kind === 'model' ? 'AI model' : 'tool'}
          </Badge>
          <div className="col-tight">
            <span className="small">
              <strong>{STEP[e.step] ?? e.step}</strong> — {e.label}
            </span>
            {detailsOf(e).length ? (
              <span className="tiny subtle">{detailsOf(e).join(' · ')}</span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  )
}

/** The same list on one line, for a footer with no room for the full version. */
export const enginesSummary = (engines) =>
  (engines ?? [])
    .map((e) => `${(STEP[e.step] ?? e.step).toLowerCase()}: ${e.label}`)
    .join(' · ')
